import {
  createColumnHelper,
  rowSelectionFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
  type RowSelectionState,
} from "@tanstack/react-table";
import { cn } from "../../lib/cn";
import { EmptyState, Loading, Th } from "./misc";

/** One feature set for every admin table: server-side filtering keeps the rest on the API. */
export const adminTableFeatures = tableFeatures({ rowSelectionFeature });
export type AdminTableFeatures = typeof adminTableFeatures;
/** Accessor and display columns carry different value types, which TanStack models with `any`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AdminColumnDef<TData extends RowData> = ColumnDef<AdminTableFeatures, TData, any>;

export const adminColumnHelper = <TData extends RowData>() =>
  createColumnHelper<AdminTableFeatures, TData>();

/**
 * Dense rows (36px) for a power-user console, a sticky header on the inset well, and a clover
 * edge on the hovered or selected row instead of zebra striping.
 */
export function DataTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  loading,
  empty = "Nothing here yet.",
  onRowClick,
  rowSelection,
  onRowSelectionChange,
}: {
  columns: AdminColumnDef<TData>[];
  data: TData[];
  getRowId: (row: TData) => string;
  loading?: boolean;
  empty?: string;
  onRowClick?: (row: TData) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (
    updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState),
  ) => void;
}) {
  const table = useTable({
    features: adminTableFeatures,
    columns,
    data,
    getRowId: (row) => getRowId(row),
    ...(rowSelection && onRowSelectionChange
      ? { state: { rowSelection }, onRowSelectionChange }
      : {}),
  });

  if (loading && data.length === 0) return <Loading />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead className="bg-well sticky top-0 z-10">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="border-line border-b">
              {group.headers.map((header) => (
                <Th key={header.id}>
                  {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                </Th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className={cn(loading && "opacity-60")}>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              data-selected={row.getIsSelected() ? "true" : undefined}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              className={cn(
                "t-row border-line/60 border-b last:border-0",
                onRowClick && "cursor-pointer",
              )}
            >
              {row.getAllCells().map((cell) => (
                <td key={cell.id} className="text-ui h-9 px-3 align-middle">
                  <table.FlexRender cell={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && !loading && <EmptyState>{empty}</EmptyState>}
    </div>
  );
}
